import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, List, X } from "@phosphor-icons/react";

const NAV = [
  { to: "/features", label: "Features" },
  { to: "/pricing", label: "Pricing" },
  { to: "/compare", label: "Compare" },
  { to: "/security", label: "Security" },
  { to: "/guides", label: "Guides" },
  { to: "/faq", label: "FAQ" },
];

export function MarketingNav() {
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  const isActive = (to) => loc.pathname === to;

  return (
    <header className="sticky top-0 z-50 bg-appbg/85 backdrop-blur-md border-b border-slate-200" data-testid="marketing-nav">
      <div className="max-w-6xl mx-auto px-6 h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center" data-testid="marketing-logo-link">
          <img src="/lottie-logo.png" alt="LOTTIE — Your Intelligent Month-End Assistant" className="h-14 w-auto object-contain" />
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {NAV.map((n) => (
            <Link key={n.to} to={n.to} data-testid={`nav-${n.label.toLowerCase()}`}
              className={`text-sm font-medium transition-colors ${isActive(n.to) ? "text-navy" : "text-navy/70 hover:text-navy"}`}>
              {n.label}
            </Link>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <Link to="/login" data-testid="nav-sign-in" className="text-sm font-medium text-navy/70 hover:text-navy transition-colors">
            Sign in
          </Link>
          <Link to="/start" data-testid="nav-start-trial"
            className="text-white text-sm font-semibold px-4 py-2 rounded-xl transition-colors flex items-center gap-1.5"
            style={{ background: "#F37A24" }}>
            Start free trial <ArrowRight size={15} weight="bold" />
          </Link>
        </div>

        <button className="md:hidden text-navy" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle">
          {open ? <X size={24} /> : <List size={24} />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-slate-200 bg-appbg px-6 py-4 space-y-3" data-testid="mobile-menu">
          {NAV.map((n) => (
            <Link key={n.to} to={n.to} onClick={() => setOpen(false)} className="block text-navy/80 font-medium">{n.label}</Link>
          ))}
          <div className="flex gap-3 pt-2">
            <Link to="/login" onClick={() => setOpen(false)} className="text-navy/80 font-medium">Sign in</Link>
            <Link to="/start" onClick={() => setOpen(false)} className="font-semibold" style={{ color: "#F37A24" }}>Start free trial →</Link>
          </div>
        </div>
      )}
    </header>
  );
}

export function MarketingFooter() {
  return (
    <footer className="bg-navy text-white mt-24" data-testid="marketing-footer">
      <div className="max-w-6xl mx-auto px-6 py-14 grid md:grid-cols-4 gap-10">
        <div className="md:col-span-2">
          <img src="/lottie-logo-white.png" alt="LOTTIE — Your Intelligent Month-End Assistant" className="h-14 w-auto object-contain mb-4" data-testid="footer-lottie-logo" />
          <p className="text-white/70 text-sm max-w-sm leading-relaxed">
            Prepayments, accruals &amp; deferred income on autopilot — connected live to Xero.
            Ledger Optimisation Through Task Intelligence &amp; Efficiency.
          </p>
        </div>
        <div>
          <div className="overline text-white/70 mb-4">Product</div>
          <ul className="space-y-2.5 text-sm text-white/65">
            <li><Link to="/features" className="hover:text-white transition-colors">Features</Link></li>
            <li><Link to="/pricing" className="hover:text-white transition-colors">Pricing</Link></li>
            <li><Link to="/compare" className="hover:text-white transition-colors">Compare</Link></li>
            <li><Link to="/security" className="hover:text-white transition-colors">Security</Link></li>
            <li><Link to="/faq" className="hover:text-white transition-colors">FAQ</Link></li>
            <li><Link to="/login" className="hover:text-white transition-colors">Sign in</Link></li>
          </ul>
        </div>
        <div>
          <div className="overline text-white/70 mb-4">Get started</div>
          <ul className="space-y-2.5 text-sm text-white/65">
            <li><Link to="/start" className="hover:text-white transition-colors">Start 7-day free trial</Link></li>
            <li><a href="mailto:hello@workwithlottie.com" className="hover:text-white transition-colors">Talk to us</a></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="max-w-6xl mx-auto px-6 py-5 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-white/70">
          <span>© {new Date().getFullYear()} WorkWithLottie. All rights reserved.</span>
          <span data-testid="footer-cass-group">
            Part of{" "}
            <a href="https://thecassgroup.co.uk" target="_blank" rel="noopener noreferrer"
              className="font-semibold text-white/90 underline underline-offset-2 hover:text-white transition-colors" data-testid="footer-cass-group-link">
              The CASS Group
            </a>
          </span>
          <span>Built for accounting practices • Powered by Xero</span>
        </div>
      </div>
    </footer>
  );
}

export default function MarketingLayout({ children }) {
  return (
    <div className="min-h-screen bg-appbg flex flex-col" data-testid="marketing-layout">
      <MarketingNav />
      <main className="flex-1">{children}</main>
      <MarketingFooter />
    </div>
  );
}
